import CONSTANTS from "core/constants";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchField, Button } from "@mindlercare/ui-web";
import { useUserHasRole } from "../../hooks/userHooks";
import useMarketStrategy from "../../strategies/market/useMarketStrategy";
import { SearchFields, SearchFieldWrapper } from "./styled";
import type { SearchTerms } from "./types";
import { termToSearchField } from "./utils";

export type PatientsSearchFormProps = {
  onSearch: Dispatch<SetStateAction<SearchTerms>>;
};

export const PatientsSearchForm = ({ onSearch }: PatientsSearchFormProps) => {
  const { t } = useTranslation();
  const isAdmin = useUserHasRole("Admin");
  const marketStrategy = useMarketStrategy();
  const searchFields = marketStrategy?.patientSearchFields ?? {};
  const [searchFieldValues, setSearchFieldValues] = useState<SearchTerms>({});

  const updateSearchTerms = <K extends keyof SearchTerms>(
    searchName: K,
    value?: SearchTerms[K],
    shouldCallOnSearch = false,
  ) => {
    const key = termToSearchField[searchName] as keyof SearchTerms;
    const updatedValue =
      key === "userId" && typeof value === "string"
        ? parseInt(value, 10)
        : value;
    const newSearchFieldValues = { ...searchFieldValues, [key]: updatedValue };
    setSearchFieldValues(newSearchFieldValues);
    if (shouldCallOnSearch) onSearch(newSearchFieldValues);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(searchFieldValues);
      }}
    >
      <SearchFields>
        {Object.values(searchFields).map((field) => {
          if (field === CONSTANTS.SEARCH_TERMS.EMAIL && !isAdmin) return null;

          return (
            <SearchFieldWrapper key={`wrapper-${field}`}>
              <SearchField
                id={`cy-patient-search-${field}`}
                key={field}
                onChange={(newValue) => {
                  updateSearchTerms(
                    field as keyof SearchTerms,
                    newValue === "" ? undefined : newValue,
                  );
                }}
                placeholder={t(field)}
              />
            </SearchFieldWrapper>
          );
        })}
        <Button
          type="submit"
          variant="primary"
          disabled={Object.keys(searchFieldValues).length === 0}
        >
          {t("SEARCH")}
        </Button>
      </SearchFields>
    </form>
  );
};
